// Tasarim seti: ilham listesinde birden cok tasarim var ve HER TASARIMIN tisort
// rengi sabit (alici tasarimi secer, renk secmez — hava durumu grubu gibi).
// AI fotograftan okur; tasarim adi, tisort rengi ve sira burada duzenlenir.

import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { clsx } from "clsx";
import { ChevronDown, ChevronUp, Layers, Loader2, Plus, Sparkles, Trash2, CheckCircle2 } from "lucide-react";
import { garmentHex } from "@erc/shared";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { useSupplierVariations } from "../../lib/useSupplierVariations";
import { DesignSetListing } from "./DesignSetListing";

type Tone = "light" | "mid" | "dark";
export interface DesignSetItem {
  key: string; title: string; color: string; hex?: string; tone: Tone;
  design?: { analysisDesignId: string; sku: string; status: "running" | "ready" | "failed"; url?: string; poolDesignId?: string; error?: string; at: string; linked?: boolean };
  mockup?: { mockupId: string; url: string };
}
export interface DesignSet {
  variationName: string; items: DesignSetItem[]; imageUrl?: string; status: "draft" | "approved"; updatedAt: string;
  collage?: { status: "running" | "ready" | "failed"; url?: string; error?: string };
  draftId?: string;
}

const TONE_LABEL: Record<Tone, string> = { light: "Açık", mid: "Orta", dark: "Koyu" };
const toneOf = (hex: string): Tone => {
  const n = parseInt(hex.slice(1), 16);
  const l = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
  return l > 0.62 ? "light" : l < 0.3 ? "dark" : "mid";
};
const inputCls = "rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200";

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/analyses${path}`, {
    method, headers: { ...authHeaders(), "Content-Type": "application/json" }, ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const j = await r.json().catch(() => null);
  if (!r.ok) throw new Error(j?.error ?? `İstek başarısız (${r.status})`);
  return j.data as T;
}

export function DesignSetPanel({ analysisId, initial, images, onSaved }: { analysisId: string; initial: DesignSet | null; images: string[]; onSaved: () => void }) {
  const [open, setOpen] = useState(Boolean(initial));
  const [set, setSet] = useState<DesignSet | null>(initial);
  const [img, setImg] = useState(initial?.imageUrl ?? images[0] ?? "");
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const vars = useSupplierVariations();
  // Rexven katalogundaki renkler (Comfort Colors once)
  const catalog = vars.hasCatalog ? vars.colorsFor(vars.brands.find((b) => /comfort/i.test(b)) ?? "", "") : [];
  useEffect(() => { if (!dirty) setSet(initial); }, [initial?.updatedAt]);

  const detect = useMutation({
    mutationFn: () => call<DesignSet>("POST", `/${analysisId}/design-set/detect`, { imageUrl: img, catalogColors: catalog }),
    onSuccess: (s) => { setErr(null); setSet(s); setDirty(false); onSaved(); },
    onError: (e) => setErr((e as Error).message),
  });
  const save = useMutation({
    mutationFn: (status: "draft" | "approved") => call<DesignSet>("PUT", `/${analysisId}/design-set`, { ...set, status }),
    onSuccess: (s) => { setErr(null); setSet(s); setDirty(false); onSaved(); },
    onError: (e) => setErr((e as Error).message),
  });
  const remove = useMutation({
    mutationFn: () => call<null>("PUT", `/${analysisId}/design-set`, { remove: true }),
    onSuccess: () => { setSet(null); setDirty(false); onSaved(); },
  });

  const edit = (fn: (s: DesignSet) => DesignSet) => { setSet((s) => (s ? fn(s) : s)); setDirty(true); };
  const setItem = (i: number, patch: Partial<DesignSetItem>) => edit((s) => ({ ...s, items: s.items.map((it, k) => (k === i ? { ...it, ...patch } : it)) }));
  const move = (i: number, d: -1 | 1) => edit((s) => {
    const items = [...s.items]; const j = i + d;
    if (j < 0 || j >= items.length) return s;
    [items[i], items[j]] = [items[j], items[i]];
    return { ...s, items };
  });

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-center justify-between gap-2 text-left">
        <span className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-indigo-500" />
          <span className="text-base font-semibold text-gray-900">Tasarım seti</span>
          {set && <span className="text-xs text-gray-500">{set.items.length} tasarım</span>}
          {set?.status === "approved" && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Onaylı</span>}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">Listede birden çok tasarım varsa ve her tasarımın tişört rengi sabitse (alıcı tasarımı seçer, rengi seçmez) burada tanımla. AI fotoğraftaki tasarımları ve tişört renklerini okur; ad, renk ve sırayı düzeltip onayla.</p>
          {images.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {images.slice(0, 10).map((u) => (
                <button key={u} onClick={() => setImg(u)} aria-pressed={img === u} title="Tasarımların göründüğü fotoğraf"
                  className={clsx("h-14 w-14 overflow-hidden rounded-lg border-2", img === u ? "border-indigo-500" : "border-transparent hover:border-gray-300")}>
                  <img src={u} alt="" loading="lazy" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => { if (set?.items.some((it) => it.design) && !window.confirm("Yeniden okumak üretilen tasarımların bağlantısını siler (tasarımlar galeride kalır). Devam edilsin mi?")) return; detect.mutate(); }} disabled={!img || detect.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
              {detect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {set ? "AI ile yeniden oku" : "AI ile oku"}
            </button>
            {!set && (
              <button onClick={() => { setSet({ variationName: "Design", items: [], status: "draft", updatedAt: "" }); setDirty(true); }}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Elle oluştur</button>
            )}
            <span className="text-xs text-gray-500">birkaç saniye</span>
          </div>

          {set && (
            <>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                Alıcının seçtiği varyasyonun adı
                <input value={set.variationName} onChange={(e) => edit((s) => ({ ...s, variationName: e.target.value }))} maxLength={30} className={`${inputCls} w-36`} />
              </label>
              <datalist id={`ds-colors-${analysisId}`}>{catalog.map((c) => <option key={c} value={c} />)}</datalist>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr><th className="px-2 py-1.5">#</th><th className="px-2 py-1.5">Tasarım</th><th className="px-2 py-1.5">Tişört rengi</th><th className="px-2 py-1.5">Ton</th><th className="px-2 py-1.5" /></tr>
                  </thead>
                  <tbody>
                    {set.items.map((it, i) => {
                      const out = catalog.length > 0 && !catalog.some((c) => c.toLowerCase() === it.color.toLowerCase());
                      return (
                        <tr key={it.key} className="border-t border-gray-100">
                          <td className="px-2 py-1 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-2 py-1"><input aria-label={`${i + 1}. tasarım adı`} value={it.title} onChange={(e) => setItem(i, { title: e.target.value })} maxLength={80} className={`${inputCls} w-full min-w-[12rem]`} /></td>
                          <td className="px-2 py-1">
                            <div className="flex items-center gap-1.5">
                              <span className="h-4 w-4 flex-shrink-0 rounded-full border border-gray-300" style={{ background: it.hex ?? garmentHex(it.color) }} />
                              <input aria-label={`${i + 1}. tişört rengi`} list={`ds-colors-${analysisId}`} value={it.color} maxLength={40}
                                onChange={(e) => { const c = e.target.value; setItem(i, { color: c, hex: undefined, tone: toneOf(garmentHex(c)) }); }}
                                className={clsx(inputCls, "w-36", out && "border-amber-300 bg-amber-50")} title={out ? "Rexven kataloğunda yok" : undefined} />
                            </div>
                          </td>
                          <td className="px-2 py-1">
                            <select aria-label={`${i + 1}. ton`} value={it.tone} onChange={(e) => setItem(i, { tone: e.target.value as Tone })} className={inputCls}>
                              {(["light", "mid", "dark"] as Tone[]).map((t) => <option key={t} value={t}>{TONE_LABEL[t]}</option>)}
                            </select>
                          </td>
                          <td className="whitespace-nowrap px-2 py-1 text-right">
                            <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Yukarı taşı" className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                            <button onClick={() => move(i, 1)} disabled={i === set.items.length - 1} aria-label="Aşağı taşı" className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                            <button onClick={() => edit((s) => ({ ...s, items: s.items.filter((_, k) => k !== i) }))} aria-label="Sil" className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {catalog.length > 0 && set.items.some((it) => !catalog.some((c) => c.toLowerCase() === it.color.toLowerCase())) && (
                <p className="text-xs text-amber-700">Sarı işaretli renkler Rexven kataloğunda yok; listeden katalog rengi seç.</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => edit((s) => ({ ...s, items: [...s.items, { key: `d${Date.now() % 100000}`, title: "", color: catalog[0] ?? "White", tone: "light" }] }))} disabled={set.items.length >= 30}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"><Plus className="h-3.5 w-3.5" /> Tasarım ekle</button>
                <button onClick={() => save.mutate("draft")} disabled={save.isPending || !dirty}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Kaydet</button>
                <button onClick={() => save.mutate("approved")} disabled={save.isPending || set.items.length === 0}
                  className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:bg-emerald-300">
                  {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Kaydet ve onayla
                </button>
                <button onClick={() => remove.mutate()} disabled={remove.isPending} className="ml-auto text-xs text-gray-500 hover:text-red-600 hover:underline">Tasarım setini kaldır</button>
              </div>
            </>
          )}
          {err && <p className="text-sm text-red-600">{err}</p>}
          {set?.status === "approved" && !dirty && <DesignSetListing analysisId={analysisId} set={set} onChanged={onSaved} />}
          {set?.status === "approved" && dirty && <p className="text-xs text-amber-700">Değişiklikleri kaydet; liste bölümü kayıtlı sete göre çalışır.</p>}
        </div>
      )}
    </section>
  );
}
